import { memo, useMemo } from 'react';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { MENUITEM_DEFINITIONS, MENUITEM_IDS, MENUITEM_MORE } from '@/lib/constants';
import { MenuBarPill } from './MenuBarPill';
import { useMenuBar } from './MenuBar.hooks';
import { useGlobalStore } from '@/stores';
import Logo from '@/assets/images/stan-logo-text.png';
import { useStanRouter } from '@/hooks/useStanRouter';
import { AccountMenu } from './AccountMenu';
import { useAskStanley } from '@/features/ask-stanley';
import { LeftMenuBarProps } from '../types';
import { cn } from '@/lib';
import { useUser } from '@/queries/useUser';
import { useCommunityUrl } from '@/features/communities/hooks/useCommunityUrl';
import { MenuItemType } from '@/types';

export const LeftMenuBar = memo(({ className }: LeftMenuBarProps) => {
    const { topMenuItems, bottomMenuItems } = useMenuBar();
    const breadcrumbs = useGlobalStore(state => state.breadcrumbs);
    const breadcrumbsSet = useMemo(() => new Set(breadcrumbs.map(bread => bread.id)), [breadcrumbs]);
    const moreMenuItem = MENUITEM_MORE;
    const { push } = useStanRouter();
    const { openChat } = useAskStanley();
    const { user } = useUser();
    const communityUrl = useCommunityUrl();

    const isPillActive = useMemo(
        () => (id: string) => {
            return breadcrumbsSet.has(id) ? 'bg-canvas' : '';
        },
        [breadcrumbsSet]
    );

    const openCommunity = () => {
        if (communityUrl) {
            window.open(communityUrl, '_blank');
        }
    };

    const onMenuItemClick = (menuItem: MenuItemType) => {
        if (menuItem.id === MENUITEM_IDS.COMMUNITY) {
            openCommunity();
        } else if (menuItem.redirect) {
            window.open(menuItem.route, '_blank');
        } else {
            push(menuItem.route);
        }
    };
    const itemVariants: Variants = {
        hidden: { opacity: 0, height: 0, scale: 0.95 },
        visible: {
            opacity: 1,
            height: 'auto',
            scale: 1,
            transition: {
                duration: 0.25,
                ease: 'easeInOut',
            },
        },
        exit: {
            opacity: 0,
            height: 0,
            scale: 0.95,
            transition: {
                duration: 0.15,
                ease: 'easeInOut',
            },
        },
    };

    return (
        <div
            className={cn(
                'bg-brand-subtle fixed z-20 flex h-svh w-[var(--spacing-left-menu-bar)] flex-col px-2 py-4 max-lg:hidden',
                className
            )}
        >
            <div className='pb-4'>
                <img
                    src={Logo}
                    alt='Stan Logo'
                    className='mx-4 mt-2 mb-4 h-6 cursor-pointer'
                    onClick={() => push(MENUITEM_DEFINITIONS[MENUITEM_IDS.HOME]?.route || '/dashboard')}
                />
            </div>
            <div className='flex w-full flex-1 flex-col gap-1 overflow-hidden'>
                <ul className='flex w-full flex-col gap-1 overflow-y-auto'>
                    <AnimatePresence initial={false}>
                        {topMenuItems.map(menuItem => (
                            <motion.li
                                key={menuItem.id}
                                variants={itemVariants}
                                initial='hidden'
                                animate='visible'
                                exit='exit'
                            >
                                <MenuBarPill
                                    text={menuItem.title}
                                    icon={menuItem.icon}
                                    redirect={menuItem.redirect}
                                    onClick={() => onMenuItemClick(menuItem)}
                                    removeFill
                                    className={isPillActive(menuItem.id)}
                                />
                            </motion.li>
                        ))}
                    </AnimatePresence>
                </ul>

                {user?.subscription_tier !== 'affiliate' && (
                    <MenuBarPill
                        text={moreMenuItem.title}
                        icon={moreMenuItem.icon}
                        onClick={() => push(moreMenuItem.route)}
                        className={isPillActive(moreMenuItem.id)}
                        removeFill
                    />
                )}
            </div>
            <ul className='mb-6 flex flex-col gap-1'>
                {bottomMenuItems.map(menuItem => {
                    const handleClick = () => {
                        if (menuItem.id === MENUITEM_IDS.ASK_STANLEY) {
                            openChat('left_menu_bar');
                        } else {
                            push(menuItem.route);
                        }
                    };

                    return (
                        <MenuBarPill
                            text={menuItem.title}
                            icon={menuItem.icon}
                            redirect={menuItem.redirect}
                            onClick={handleClick}
                            className={isPillActive(menuItem.id)}
                            key={menuItem.id}
                            removeFill={menuItem.removeFill}
                        />
                    );
                })}
                <AccountMenu />
            </ul>
        </div>
    );
});
